/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Button } from "../ui/button";
import { actionList } from "./ActionHelper";
import { useStoreDispatch } from "../../store/hooks";
import { DIALOGOPENFOR } from "../../globals/globals";
import { useSelector } from "react-redux";
import { setDeviceAction } from "../../store/api/devices/reducers";
import { IDeviceAction, IActionProps } from "../../store/api/devices/types";
import BasicDialog from "../Shared/Dialogs/BasicDialog";
import { basicDialogSelector } from "../../store/helpers/helperSelectors";
import { setBasicDialogOpenFor } from "../../store/helpers/helperReducers";
import { useTaskAction, useTaskActionComponent, useGeneralAction } from "./hooks";
import { Action } from "./types";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { Icon } from "../Shared/Icon";

interface IActionPropsVal {
  actionProps: IActionProps;
  size?: "small" | "large" | "medium" | undefined;
  disabled?: boolean;
  tableId?: string;
  variant?: "default" | "dark";
  triggerType?: "button" | "gridActionMenu";
}
const Actions: React.FC<IActionPropsVal> = ({ actionProps, size = "large", disabled = false, tableId = "", variant = "default", triggerType = "button" }) => {
  const { taskActionGeneralSubmitHandler } = useTaskAction(tableId || "");
  const { taskActionSubmitHandler } = useTaskActionComponent();
  const { actionSubmitHandler } = useGeneralAction(tableId || "");
  const dispatch = useStoreDispatch();

  const [selectedAction, setSelectedAction] = React.useState<Action | null>(null);
  const basicDialodOpenFor = useSelector(basicDialogSelector);
  
  const handleOnSelect = (value: any) => {
    if (value === "ViewDetails") {
      const deviceKey = actionProps?.deviceKey;
      if (deviceKey) {
        window.location.assign(`/device/${encodeURIComponent(deviceKey)}`);
      }
      return;
    }
    const actionPerformed = actionList[value];

    if (!actionPerformed) {
      setSelectedAction(null);
      dispatch(setBasicDialogOpenFor(undefined));
      return;
    }

    const deviceAction: IDeviceAction = {
      ...actionPerformed,
      deviceKey: actionProps?.deviceKey,
      selectedData: actionProps?.selectedData,
    };

    if (deviceAction?.isTask && !deviceAction.isComponentRequired) {
      dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ACTIONGENERALTASK));
    } else if (deviceAction?.isTask && deviceAction.isComponentRequired) {
      dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ACTIONTASK));
    } else {
      dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ACTION));
    }

    setSelectedAction(actionPerformed);
    dispatch(setDeviceAction(deviceAction));
  };

  const submitHandler = (event: any) => {
    if (selectedAction?.isTask && !selectedAction.isComponentRequired) {
      // if task has no component to render
      taskActionGeneralSubmitHandler();
    } else if (selectedAction?.isTask && selectedAction.isComponentRequired) {
      taskActionSubmitHandler();
    } else {
      actionSubmitHandler();
    }
    setSelectedAction(null);
    dispatch(setBasicDialogOpenFor(undefined));
  };
  const isEnableSave = true;

  const handleCancelAction = () => {
    setSelectedAction(null);
    dispatch(setBasicDialogOpenFor(undefined))
  }

  const dialogActions = (
    <div className="flex justify-end items-center pt-2 pb-2 gap-2 mr-3">
      <Button variant="outline" onClick={() => handleCancelAction()}>Cancel</Button>
      <Button disabled={!isEnableSave} onClick={submitHandler}>
        Execute
      </Button>
    </div>
  );

  const isDisabled = !actionProps.actionList || actionProps.actionList.length === 0 || disabled;
  const visibleActionList = (actionProps.actionList || []).filter((item: any) => {
    if (triggerType !== "gridActionMenu") {
      return true;
    }

    const actionValue = String(item?.value || "").toLowerCase();
    return actionValue !== "rebootservicesnode" && actionValue !== "reboot";
  });

  const menuActionList = React.useMemo(() => {
    if (triggerType !== "gridActionMenu") {
      return visibleActionList;
    }

    const hasViewDetails = visibleActionList.some((item: any) => item?.value === "ViewDetails");
    if (hasViewDetails) {
      return visibleActionList;
    }

    return [{ text: "View Details", value: "ViewDetails" }, ...visibleActionList];
  }, [triggerType, visibleActionList]);


  const shouldShowDialog =
    !!selectedAction &&
    (basicDialodOpenFor === DIALOGOPENFOR.ACTIONTASK ||
      basicDialodOpenFor === DIALOGOPENFOR.ACTIONGENERALTASK ||
      basicDialodOpenFor === DIALOGOPENFOR.ACTION);
  return (
    <>
      {shouldShowDialog && basicDialodOpenFor === DIALOGOPENFOR.ACTIONTASK && (
        <BasicDialog headerTitle={`${selectedAction?.title }`} size="sm" dialogActions={dialogActions}>
          {selectedAction?.component}
        </BasicDialog>
      )}
      { shouldShowDialog && basicDialodOpenFor === DIALOGOPENFOR.ACTIONGENERALTASK && (
        <BasicDialog headerTitle={`${selectedAction?.title }`} size="sm" dialogActions={dialogActions}>
          <div className="pl-5">
            {selectedAction?.dialogue}
          </div>
        </BasicDialog>
      )}
      { shouldShowDialog && basicDialodOpenFor === DIALOGOPENFOR.ACTION && (
        <BasicDialog headerTitle={`${selectedAction?.title }`} size="sm" dialogActions={dialogActions}>
          <div className="pl-5">
            {selectedAction?.dialogue}
          </div>
        </BasicDialog>
      )}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          {triggerType === "gridActionMenu" ? (
            <button
              type="button"
              disabled={isDisabled}
              aria-label="Row actions"
              className="inline-flex items-center justify-center p-1 bg-transparent hover:bg-accent rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="more_vert" size={18} className="text-foreground" />
            </button>
          ) : (
            <button
              type="button"
              disabled={isDisabled}
              className={`inline-flex items-center rounded-md border shadow-sm px-3 py-1.5 text-xs font-medium disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${variant === "dark"
                ? "border-gray-600 bg-transparent text-white hover:bg-gray-800 hover:text-white"
                : "border-input bg-card text-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
              style={{ height: "32px" }}
            >
              Actions
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ml-1"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={triggerType === "gridActionMenu" ? "min-w-[125px] z-[1801]" : "min-w-[85px] z-[1801]"}>
          {menuActionList.map((item: any) => {
              const actionLabel = String(item?.value || item?.text || "").toLowerCase();
              const isDeleteAction = actionLabel.includes("delete");
              return (
              <DropdownMenuItem
                key={item.value}
                className={`cursor-pointer ${(isDeleteAction && triggerType === "gridActionMenu") ? "text-destructive focus:text-destructive data-[highlighted]:text-destructive" : ""}`}
                onSelect={() => handleOnSelect(item.value)}
              >
                {item.text}
              </DropdownMenuItem>
            )})}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default Actions;
